import clsx from "clsx";
import { Link } from "react-router-dom";

import type { Id } from "../../../api/types";
import { Card, EmptyState, Pill } from "../../../ui";
import { STATE_WORDS } from "../../../ui/state";
import { FILTER_STATES } from "../model/state";
import { type NavModel, type NavNode } from "../model/nav";
import { navigatorHref } from "../model/words";

function itemsBelow(model: NavModel, root: NavNode): NavNode[] {
  const out: NavNode[] = [];
  const walk = (node: NavNode) => {
    for (const child of model.childrenOf.get(node.id) ?? []) {
      if (child.kind === "item") out.push(child);
      walk(child);
    }
  };
  walk(root);
  return out;
}

/**
 * A small live map of the discipline: one dot for each item, grouped by library, in the colour of its state.
 * A proposal is dashed. New items pop in as colleagues add them. Each dot opens the item in the navigator.
 */
export function HomeMiniMap({ model, arrivals }: { model: NavModel; arrivals?: ReadonlySet<Id> }) {
  const groups = model.roots.map((root) => ({ root, items: itemsBelow(model, root) })).filter((group) => group.items.length > 0 || model.roots.length === 1);
  const total = groups.reduce((sum, group) => sum + group.items.length, 0);
  return (
    <Card aria-labelledby="minimap-title" className="nv-minimap">
      <h2 id="minimap-title">The ontology at a glance</h2>
      {total === 0 ? (
        <EmptyState title="No items yet">Each item your team adds appears here as a dot, in the colour of its state.</EmptyState>
      ) : (
        <>
          {groups.map(({ root, items }) => (
            <section key={root.id} className="nv-minimap__group" aria-label={root.label}>
              <h3 className="nv-minimap__title">{root.label}</h3>
              <ul className="nv-dots">
                {items.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={navigatorHref(item.id)}
                      className={clsx("nv-dot", arrivals?.has(item.id) && "nv-dot--new")}
                      data-state={item.state ?? "none"}
                      aria-label={`${item.label}${item.state ? `, ${STATE_WORDS[item.state]}` : ""}`}
                      title={item.label}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
          <ul className="nv-minimap__legend row" aria-label="What the colours mean">
            {FILTER_STATES.map((state) => (
              <li key={state}>
                <Pill tone={state}>{STATE_WORDS[state]}</Pill>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
